import _ from 'lodash'

// A route template with a locked address order puts the addresses of its connected order templates in that order and
// locks them. An order template with a fixed address order needs its addresses in their own order (index 0 before 1),
// otherwise the locked route breaks the order of that order and no new stop can be placed in it.

type ConnectedAddress = {id?: string, index?: number}

type FixedOrderTemplate = {_id?: string, name?: string, addressOrder?: string}

type FixedOrderRouteTemplate = {
    lockRecurringOrdersAddressesOrder?: boolean
    connectedRecurringOrders?: string[]
    connectedRecurringOrdersAddresses?: ConnectedAddress[]
}

// The id of a connected address is `${orderTemplateId}_${addressIndex}`
export function parseAddressId(id: string): {orderTemplateId: string, addressIndex: number} {
    const separator = id.lastIndexOf('_')

    return {orderTemplateId: id.slice(0, separator), addressIndex: parseInt(id.slice(separator + 1))}
}

/**
 * The ids of the connected addresses that come before an address of the same order template with a lower index, for
 * order templates with a fixed address order. connectedAddresses are in the order of the route template (by index).
 */
export function addressesOutOfFixedOrder(connectedAddresses: ConnectedAddress[], orderTemplates: FixedOrderTemplate[]): string[] {
    const fixed = new Set(orderTemplates.filter((orderTemplate) => orderTemplate.addressOrder === 'fixed').map((orderTemplate) => String(orderTemplate._id)))
    const ordered = connectedAddresses.filter((address) => !!address.id).sort((a, b) => (a.index ?? 0) - (b.index ?? 0)).map((address) => ({id: address.id, ...parseAddressId(address.id)}))

    return ordered
        .filter((address, position) => fixed.has(address.orderTemplateId) && ordered.slice(position + 1).some((later) => {
            return later.orderTemplateId === address.orderTemplateId && later.addressIndex < address.addressIndex
        }))
        .map((address) => address.id)
}

/** The connected order templates the locked address order of the route template puts out of their fixed order. */
export function orderTemplatesOutOfFixedOrder<T extends FixedOrderTemplate>(routeTemplate: FixedOrderRouteTemplate, orderTemplates: T[]): T[] {
    if (!routeTemplate.lockRecurringOrdersAddressesOrder || !routeTemplate.connectedRecurringOrdersAddresses?.length) {
        return []
    }

    const connected = orderTemplates.filter((orderTemplate) => routeTemplate.connectedRecurringOrders?.includes(String(orderTemplate._id)))
    const outOfOrderIds = new Set(addressesOutOfFixedOrder(routeTemplate.connectedRecurringOrdersAddresses, connected).map((id) => parseAddressId(id).orderTemplateId))

    return connected.filter((orderTemplate) => outOfOrderIds.has(String(orderTemplate._id)))
}

/**
 * Looks up the place of an address of an order template in the locked address order of a route template: its position
 * in connectedAddresses. An address that is not in there (added to the order template after the route template was
 * saved) goes right after the address of the same order template with the highest lower index, or right before its
 * first address when it has none, so the order template keeps its own order. -1 when the order template has no address
 * in connectedAddresses at all.
 */
export function lockedAddressPositions(connectedAddresses: ConnectedAddress[]): (orderTemplateId: string | undefined, addressIndex: number) => number {
    const positions = new Map<string, number>()
    const byOrderTemplate = new Map<string, {position: number, addressIndex: number}[]>()

    connectedAddresses.forEach((address, position) => {
        if (!address.id) {
            return
        }

        const {orderTemplateId, addressIndex} = parseAddressId(address.id)

        positions.set(address.id, position)
        byOrderTemplate.set(orderTemplateId, [...byOrderTemplate.get(orderTemplateId) || [], {position, addressIndex}])
    })

    return (orderTemplateId, addressIndex) => {
        const position = positions.get(`${orderTemplateId}_${addressIndex}`)

        if (position !== undefined) {
            return position
        }

        const known = byOrderTemplate.get(String(orderTemplateId))

        if (!known) {
            return -1
        }

        // Between two positions, in the order of the address index when several addresses were added
        const offset = 1 / (addressIndex + 2)
        const earlier = known.filter((address) => address.addressIndex < addressIndex)

        if (earlier.length > 0) {
            return _.maxBy(earlier, 'addressIndex').position + 1 - offset
        }

        return Math.min(...known.map((address) => address.position)) - offset
    }
}

/**
 * The addresses of a route in the locked address order of its route template. Addresses without a place in it (a stop
 * that is not from a connected order template, or from one the route template has no addresses of yet) go after the
 * others. Addresses with the same place keep the order they had.
 */
export function sortByLockedAddressOrder<T extends {orderId?: string, index?: number}>(addresses: T[], orderTemplateIds: Record<string, string | undefined>, connectedAddresses: ConnectedAddress[]): T[] {
    const lockedPosition = lockedAddressPositions(connectedAddresses)

    return addresses
        .map((address, original) => {
            const orderTemplateId = address.orderId ? orderTemplateIds[address.orderId] : undefined
            const position = orderTemplateId ? lockedPosition(orderTemplateId, address.index ?? 0) : -1

            return {address, original, position: position === -1 ? Infinity : position}
        })
        .sort((a, b) => a.position === b.position ? a.original - b.original : a.position < b.position ? -1 : 1)
        .map(({address}) => address)
}
